"use client";

import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import Image from "next/image";
import {  useSearchParams } from "next/navigation";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import useSWRMutation from "swr/mutation";
import { toast } from "sonner";
import getErrorMessage from "@/lib/get-error";
import { fetcher } from "@/lib/fetcher";
import dayjs from "dayjs";
import useLocaleLegacy from "@/hooks/use-locale-legacy";
import otpLocale from "@/locales/otp.json";
import inquriyLocale from "@/locales/inquiry.json";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import formatTime from "@/lib/format-time";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import useCleanup from "@/hooks/use-cleanup";
import { utm_query_params } from "../../models/utm";
import { useOTP } from "@/hooks/use-otp";
import { useRouter } from "@/src/i18n/navigation";
import { useLocale } from "next-intl";

export default function Client() {
  const lang = useLocale()
  const router = useRouter();
  const qs = useSearchParams();

  const { t } = useLocaleLegacy(otpLocale);
  const { t: inquiryLocale } = useLocaleLegacy(inquriyLocale);

  const otp = useRef("");

  const [showOTP, setShowOTP] = useState(false);
  const [timer, setTimer] = useState(0);

  const cleanup = useCleanup();
  const {
    sendOTP,
    validateOTP,
  } = useOTP()

  const form = useForm({
    defaultValues: {
      full_name: "",
      email: "",
      phone: "",
      date: dayjs().toDate(),
      consent: false,
    },
  });

  const [showSuccess, setShowSuccess] = useState(false);

  const { trigger: submitInquiry } = useSWRMutation(
    "/nxapi/inquiries",
    async function (url, { arg }: { arg: Record<string, any> }) {
      return await fetcher(url, {
        method: "POST",
        body: JSON.stringify(arg),
        headers: {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_API_TOKEN}`,
          "Content-Type": "application/json",
        },
      });
    }
  );

  const onSubmit = (values: Record<string, any>) => {
    sendOTP({
      phone: values.phone,
    }).catch((err) => {
      toast.error(String(getErrorMessage(err).error));
    });
    setShowOTP(true);
  };

  const onSendOTP = async () => {
    try {
      const response = await validateOTP({
        phone: form.getValues().phone,
        otp_code: otp.current,
      });

      if (response.status === "error") {
        throw new Error(response.message);
      }

      const { consent, ..._values } = form.getValues();

      const utmParams = utm_query_params.reduce((acc, key) => {
        if (!qs?.has(key)) return acc;
        return {
          ...acc,
          [key]: qs.get(key) || "",
        };
      }, {});

      await submitInquiry({
        full_name: _values.full_name,
        email: _values.email,
        date: _values.date,
        phone: form.getValues().phone,
        ...utmParams,
      });
      form.reset();
      setShowSuccess(true);
    } catch (error) {
      toast.error(String(getErrorMessage(error).error));
    }
  };

  const onResendOTP = () => {
    sendOTP({
      phone: form.getValues().phone,
    }).then((res) => {
      setTimer(res.countdown_in_seconds);
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev - 1 < 0) {
            clearInterval(interval);
            return prev;
          }
          return prev - 1;
        });
      }, 1_000); //1seconds;

      cleanup(() => clearInterval(interval));
    });
  };

  return (
    <>
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Success</DialogTitle>
            <DialogDescription>
              Your data is secured, our marketing agent will reach out soon.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <Dialog open={showOTP} onOpenChange={setShowOTP}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-center">{t("title")}</DialogTitle>
            <DialogDescription asChild>
              <div className="flex flex-col items-center gap-y-3">
                <p>{t("description")}</p>

                <InputOTP
                  maxLength={6}
                  pattern={REGEXP_ONLY_DIGITS}
                  className="mt-4"
                  onChange={(e) => {
                    otp.current = e;
                  }}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>

                <button
                  className="rounded-sm px-4 py-1 bg-gold text-white cursor-pointer"
                  onClick={onSendOTP}
                >
                  {t("verify_btn")}
                </button>
                <div>
                  {t("receive_otp")}
                  <button
                    disabled={timer > 0}
                    className="ml-1 cursor-pointer disabled:cursor-not-allowed underline"
                    onClick={onResendOTP}
                  >
                    {t("resend_otp")}

                    {timer > 0 && <span> ({formatTime(timer)})</span>}
                  </button>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <div className="bg-brown min-h-screen overflow-y-auto">
        <div className="w-full xl:max-w-screen-xl mx-auto">
          <div className="pt-36 xl:pt-20 px-12 xl:px-0 w-full xl:w-1/2">
            <h4 className="font-pluto text-xl xl:text-4xl text-white xl:leading-10" dangerouslySetInnerHTML={{__html : inquiryLocale("book_title")}}>
            </h4>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-y-3 mt-9">
                  <FormField
                    control={form.control}
                    name="full_name"
                    rules={{
                      required: `Name is required`,
                    }}
                    render={({ field: filedInput }) => (
                      <FormItem>
                        <FormControl>
                          <div className="xl:rounded-[68px] xl:px-9 py-3 xl:py-6 border-b xl:border border-gold flex">
                            <input
                              {...filedInput}
                              type="text"
                              placeholder={`${inquiryLocale("name_placeholder")}*`}
                              className="xl:text-xl text-white flex-1 focus:outline-0 font-karbon placeholder:text-white"
                            />
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500 font-medium" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    rules={{
                      required: `Email is required`,
                    }}
                    render={({ field: filedInput }) => (
                      <FormItem>
                        <FormControl>
                          <div className="xl:rounded-[68px] xl:px-9 py-3 xl:py-6 border-b xl:border border-gold flex">
                            <input
                              {...filedInput}
                              type="text"
                              name=""
                              placeholder={`${inquiryLocale("email_placeholder")}*`}

                              id=""
                              className="xl:text-xl text-white flex-1 focus:outline-0 font-karbon placeholder:text-white"
                            />
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500 font-medium" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    rules={{
                      required: `Phone Number is required`,
                    }}
                    render={({ field: filedInput }) => (
                      <FormItem>
                        <FormControl>
                          <div className="xl:rounded-[68px] xl:px-9 py-3 xl:py-6 border-b xl:border border-gold flex">
                            <input
                              {...filedInput}
                              type="text"
                              name=""
                              placeholder={`${inquiryLocale("phone_number_placeholder")}*`}
                              id=""
                              onKeyDown={e =>{
                                if (
                                  [ 'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                                    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown' ].indexOf(e.key) !== -1 ||
                                  // Allow: Ctrl/Cmd+A,C,V,X,Z (for copy/paste/select all)
                                  ((e.ctrlKey || e.metaKey) && ['a','c','v','x','z'].includes(e.key.toLowerCase()))
                                ) {
                                  return; // let it happen
                                }
                                // Block anything that's not 0–9
                                if (!/^[0-9]$/.test(e.key)) {
                                  e.preventDefault();
                                  return
                                }
                              }}
                              className="xl:text-xl text-white flex-1 focus:outline-0 font-karbon placeholder:text-white"
                            />
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500 font-medium" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="date"
                    rules={{
                      required: `Date is required`,
                    }}
                    render={({ field: filedInput }) => (
                      <FormItem>
                        <FormControl>
                          <div className="xl:rounded-[68px] xl:px-9 py-3 xl:py-6 border-b xl:border border-gold flex">
                            <input
                              {...filedInput}
                              placeholder="Select Date*"
                              value={filedInput.value?.toLocaleDateString()}
                              className="xl:text-xl flex-1 text-white focus:outline-0 font-karbon placeholder:text-white"
                            />

                            <Popover>
                              <PopoverTrigger asChild>
                                <img
                                  src="/calender.png"
                                  className="cursor-pointer w-5 xl:w-9 h-5 xl:h-9"
                                  alt=""
                                />
                              </PopoverTrigger>
                              <PopoverContent>
                                <Calendar
                                  mode="single"
                                  onSelect={(date) => {
                                    filedInput.onChange(date);
                                  }}
                                  initialFocus
                                  disabled={date => {
                                    return date < new Date()
                                  }}
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500 font-medium" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="consent"
                    rules={{
                      required: `Consent is required`,
                    }}
                    render={({ field: filedInput }) => (
                      <FormItem>
                        <FormControl>
                          <div className="flex items-center space-x-2 mt-5">
                            <Checkbox
                              id="terms"
                              checked={filedInput.value}
                              onCheckedChange={filedInput.onChange}
                            />
                            <label
                              htmlFor="terms"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white font-karbon"
                              dangerouslySetInnerHTML={{
                                __html: `${inquiryLocale("consent_label")}*`
                              }}
                            >
                            </label>
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500 font-medium" />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex flex-col xl:flex-row items-center justify-between mt-12 mb-12">
                  <button
                    type="submit"
                    className="bg-gold text-white uppercase rounded-[52px] py-1 xl:py-3.5 px-8 font-bold font-pluto cursor-pointer text-[12px] xl:text-base"
                  >
{inquiryLocale("submit_btn")}
                  </button>

                  <button
                    type="button"
                    className="font-medium text-white inline-flex items-center cursor-pointer mt-20 xl:mt-0"
                    onClick={() => router.push("/", {locale: lang})}
                  >
                    <Image
                      src="/turn-left.png"
                      alt=""
                      width={31}
                      height={31}
                      className="w-3.5 xl:w-7 h-3.5 xl:h-7"
                    />
                    <span className="font-karbon ml-1 text-[14px] xl:text-lg">
                    {inquiryLocale("back_to_homepage")}
                    </span>
                  </button>
                </div>
              </form>
            </Form>
          </div>
        </div>

        {/* <div className="absolute top-0 right-0 h-full hidden xl:block">
          <img
            src="/inquiry/watermark.png"
            className="h-full object-cover"
            alt=""
          />
        </div> */}
      </div>
    </>
  );
}
